"use client"

import { useCallback, type ReactNode } from "react"
import dynamic from "next/dynamic"
import { PopupLayout } from "@/components/common/popup/layout/PopupLayout"
import challengeApply from "@/assets/lottie/challenge_apply.json"
import {
  PromotionEventPopupComponentProps,
  resolvePromotionEventHeaderCode,
  type EventActionType,
} from "../EventConfig"
import { useNewsNavigation } from "@/app/(tabs)/(home)/_components/HomeNewsSection/_hooks/useNewsNavigation"

// 💡 동적으로 import하고 SSR을 비활성화 (기존 PromotionPopup/ChallengeCompletePopup 패턴)
const Lottie = dynamic(() => import("lottie-react"), { ssr: false })

/**
 * 미션별 완료 문구. 화면 코드(문구)는 훅이 아닌 팝업 컴포넌트에서 관리한다.
 */
const MISSION_ACTION_MESSAGES: Partial<Record<EventActionType, ReactNode>> = {
  book: (
    <>
      &lt;나만 아는 비밀 도서&gt; EVENT
      <br />
      <b>첫 번째 미션</b> 참여가 완료되었습니다!
      <br />
      <br />
      이어서 비밀 도서의 봉인 해제를 하세요.
      <br />
      (*도서 표지 클릭 후 추천사 쓰기)
    </>
  ),
  recommendation: (
    <>
      &lt;나만 아는 비밀 도서&gt; EVENT
      <br />
      <b>두 번째 미션</b> 참여가 완료되었습니다!
    </>
  ),
}

/**
 * 이벤트 미션 완료 팝업 (미션 1: 도서 등록 / 미션 2: 추천사 등록).
 *
 * UI(Lottie / 확인 버튼)는 챌린지 완료 팝업(ChallengeCompletePopup)과 동일하게 재사용하고,
 * 문구만 이벤트 전용으로 분리한다.
 *
 * 미션별 팝업 2회 노출 모델:
 * - 미션 1(도서 등록) 완료 시 "미션 1/2 완료"
 * - 미션 2(추천사 등록) 완료 시 "미션 2/2 완료"
 *
 * checkEvent의 config.component 경로로 열릴 때 action props로
 * 어떤 미션(액션)이 완료됐는지 전달받아 해당 문구와 옵션을 노출한다.
 */
const PromotionPopup475 = ({
  onClose,
  action,
  confirmLabel = "확인",
  navigateToEvent,
}: PromotionEventPopupComponentProps) => {
  const { navigate } = useNewsNavigation()

  const onConfirm = useCallback(() => {
    if (navigateToEvent) {
      const headerCode = resolvePromotionEventHeaderCode("475")
      navigate(`/dgt/web/event/event-detail/${headerCode}?gubn=ing`)
    }
    onClose()
  }, [navigateToEvent, navigate, onClose])

  const message =
    (action ? MISSION_ACTION_MESSAGES[action] : undefined) ??
    "미션이 완료되었습니다!"
    
  return (
    <PopupLayout
      buttons={
        action === "recommendation" ? [
          {
            title: "닫기",
            onClick: onClose,
          },
          {
            title: confirmLabel,
            onClick: onConfirm,
          }
        ] : [
          {
              title: confirmLabel,
              onClick: onConfirm,
          }
        ]  
      }
    >
      <div className="flex flex-col items-center justify-center">
        <div className="flex h-[106px] w-[106px] items-center justify-center">
          <Lottie
            animationData={challengeApply}
            loop={false}
            style={{ width: 106, height: 106 }}
          />
        </div>
        <span className="typo-rl-body2 mt-4 text-center text-rl-gray-font-press">
          {message}
        </span>
      </div>
    </PopupLayout>
  )
}

export default PromotionPopup475
